import { LeaseAgreementStatusTypesEnum, LeaseApplicationReferenceTypesEnum } from "@propertelligent/client-api";
import { resolveOAuthOrigin } from "./electron/desktop";

export const debouncedFunction = (
  fn: (...args: any[]) => void,
  delay: number
) => {
  let timer: string | number | NodeJS.Timeout;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
};

export const mapArrayFields = (entityArr: any[], fieldName: string) => {
  entityArr?.map((item) => {
    item["label"] = item?.[fieldName];
    item["value"] = item?.id;
  });
  return entityArr;
};

export const insertUniqKeyInArr = (rows: any[]) => {
  rows?.map((item, index) => Object.assign(item, { id: index }));
  return rows;
};

export const isValidPositiveNumber = (val: any): number | null => {
  return Number(val) && Number(val) > 0 ? Number(val) : null;
};

export const convertStreamAndDownload = (stream: string, fileName: string) => {
  let byteCharacters = atob(stream);
  let byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  let byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/octet-stream",
  }); //Type is important
  const url = window.URL.createObjectURL(blob);

  // Creating the hyperlink and auto click it to start the download
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
};

export const convertStreamAndDownloadBymimeTypes = (stream: string, fileName: string) => {

  // Remove 'data:mime/type;base64,' if present
  const base64String = stream.includes(";base64,") ? stream.split(";base64,")[1] : stream;

  // Extract the first 12 characters for better MIME type detection
  const base64Header = base64String.substring(0, 12);

  const mimeTypes: { [key: string]: string } = {
    JVBERi0: "application/pdf",
    R0lGODdh: "image/gif",
    R0lGODlh: "image/gif",
    iVBORw0KGgo: "image/png",
    "/9j/": "image/jpeg",
  };

  let mimeType = "application/octet-stream";
  for (const signature in mimeTypes) {
    if (base64Header.startsWith(signature)) {
      mimeType = mimeTypes[signature];
      break;
    }
  }

  // Convert Base64 to binary
  try {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create Blob
    const blob = new Blob([byteArray], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    // Determine file extension from MIME type
    const extensionMap: { [key: string]: string } = {
      "application/pdf": "pdf",
      "image/png": "png",
      "image/jpeg": "jpg",
      "image/gif": "gif",
    };
    const fileExtension = extensionMap[mimeType] || "bin"; // Default to .bin if unknown

    // Create a download link
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName.includes(".") ? fileName : `${fileName}.${fileExtension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error while converting Base64 to file:", error);
  }
};


export const formatString = (input) => {
  return input.replace(/([a-z])([A-Z])/g, '$1-$2');
}

export const formatWithSpaces = (value) => {
  if (!value) return '';

  // Remove all non-numeric characters
  const cleanedValue = value.replace(/\D/g, '');

  // Add space after every 4 digits
  let formattedString = '';
  for (let i = 0; i < cleanedValue.length; i++) {
    if (i > 0 && i % 4 === 0) {
      formattedString += ' ';
    }
    formattedString += cleanedValue[i];
  }

  return formattedString;
};


export const convertENUMtoJSONArray = <
  T extends Record<string, string | number>
>(
  ENUM: T
) => {
  const doubleArr = Object.keys(ENUM).map((key) => ({
    id: ENUM[key],
    name: key,
  }));
  const jsonArr =
    Array.isArray(doubleArr) && doubleArr.length > 0
      ? doubleArr.slice(doubleArr.length / 2, doubleArr.length)
      : [];
  return jsonArr;
};

// export const convertENUMtoJSONArrayWithSpace = <
//   T extends Record<string, string | number>
// >(
//   ENUM: T
// ) => {
//   const doubleArr = Object.keys(ENUM).map((key) => ({
//     id: ENUM[key],
//     name: key.replace(/([a-z])([A-Z])/g, "$1 $2"),
//   }));
//   const jsonArr =
//     Array.isArray(doubleArr) && doubleArr.length > 0
//       ? doubleArr.slice(doubleArr.length / 2, doubleArr.length)
//       : [];
//   return jsonArr;
// };

export const convertENUMtoJSONArrayWithSpace = <
  T extends Record<string, string | number>
>(
  ENUM: T
) => {
  const keys = Object.keys(ENUM).filter((k) => isNaN(Number(k))); // Filter out numeric keys

  const arr = keys.map((key) => ({
    id: ENUM[key],
    name: key
      .replace(/([a-z])([A-Z0-9])/g, "$1 $2") // space between lowercase and uppercase/number
      .replace(/([0-9])([A-Z])/g, "$1 $2")   // space between number and uppercase
  }));

  return arr;
};

// Sorts dropdown/select options alphabetically by label, forcing any option literally labeled
// "Other" (case-insensitive, trimmed) to the bottom regardless of where it falls alphabetically.
// Non-mutating: several call sites pass props/state directly in, so sorting in place risks
// subtle re-render bugs. `getLabel` is caller-supplied so one helper covers every options shape
// in the codebase ({Id, Name}, {id, description}, plain string[], ...) without per-shape overloads.
export const sortOptionsAlphabeticallyOtherLast = <T,>(
  options: T[],
  getLabel: (option: T) => string
): T[] => {
  const isOther = (label: string) => (label ?? "").trim().toLowerCase() === "other";
  return [...options].sort((a, b) => {
    const la = getLabel(a) ?? "";
    const lb = getLabel(b) ?? "";
    const aOther = isOther(la);
    const bOther = isOther(lb);
    if (aOther && bOther) return 0;
    if (aOther) return 1;
    if (bOther) return -1;
    return la.localeCompare(lb, undefined, { sensitivity: "base", numeric: true });
  });
};

export const getSizeString = (size: string) => {
  return size ? size + " Sqft" : "- Sqft"
}

export function getEnumKeyByValue<T>(enumObj: T, value: number): string | undefined {
  const entry = Object.entries(enumObj).find(([_, v]) => v === value);
  return entry ? entry[0] : undefined;
}

// In Electron the renderer origin is app:// (or the dev server), which is not a
// valid OAuth redirect target and isn't registered on the backend's Google
// client. resolveOAuthOrigin pins it to the dev web origin so the Gmail-connect
// redirect_uri matches on both the consent request and the backend token
// exchange. (Pure resolver lives in ./electron/desktop and is unit-tested.)
export const currentUrl = resolveOAuthOrigin(
  typeof navigator !== "undefined" ? navigator.userAgent : "",
  typeof window !== "undefined" ? window.location.origin : ""
);

export const staticTemplates = {
  currentAddress: {
    unitNumber: "205",
    streetAddress: "5250 Truman Dr",
    city: "Decatur",
    state: "GA",
    zip: "30035",
    moveOutDate: "Jun 29, 2023",
    moveOutReasonTypeId: "Better Neighborhood",
  },
  basicDetails: {
    firstName: "Murtaza",
    lastName: "Abdeali",
    email: "murtaza.abdeali@gmail.com",
    phoneNumber: "+1-111-222-3333"
  },
  creditCheck: {
    taxIdNumber: "123-123-1234",
    dateOfBirth: "Jun 29, 2001",
    consentToRunCheck: true,
  },
  contact: {
    firstName: "Murtaza",
    lastName: "Abdeali",
    email: "murtaza.abdeali@gmail.com",
    phoneNumber: "+1-111-222-3333",
    name: "Propertelligent",
    startDate: "Jun 29, 2023",
    endDate: "Jun 29, 2023",
  },
  reference: {
    firstName: "Khurram",
    lastName: "Hameed",
    email: "khurram@gmail.com",
    phoneNumber: "+1-111-222-3333",
    referenceTypeId: LeaseApplicationReferenceTypesEnum.Landlord,
  },
  employment: {
    name: "Hill & Associates",
    startDate: "2024-11-30",
    endDate: "2024-11-31",
    firstName: "Khurram",
    lastName: "Hameed",
    email: "khurram@gmail.com",
    phoneNumber: "+1-111-222-3333",
  },
};

export function utcDateFormat(dateString) {
  if (!dateString) {
    return
  }
  // Parse the specific date
  const specificDate = new Date(dateString);

  // Get the current time in UTC
  const now = new Date();

  // Set the specific date to have the current UTC time
  specificDate.setUTCHours(now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds(), now.getUTCMilliseconds());

  // Return the date in ISO format
  return specificDate.toISOString();
}

export const formatWithCommas = (value) => {
  const num = Number(value);
  if (isNaN(num) || value === null || value === undefined || value === '') return "0.00";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const leaseAgreementStepLabels = [
  "Send Agreement",
  "Tenant Signature",
  "Payments",
  "Landlord Signature",
];

export const getLeaseAgreementSteps = (statusId?: number) => {
  // Map enum values to how many steps should be completed
  const statusToStepIndex: Record<number, number> = {
    [LeaseAgreementStatusTypesEnum.PendingTenantSignature]: 1,
    [LeaseAgreementStatusTypesEnum.PendingPayment]: 2,
    [LeaseAgreementStatusTypesEnum.PendingLandlordSignature]: 3,
    [LeaseAgreementStatusTypesEnum.Signed]: 4,
  };

  const completedCount = statusToStepIndex[statusId ?? 0] ?? 0;

  return leaseAgreementStepLabels.map((label, index) => ({
    label,
    completed: index < completedCount, // mark completed if index < allowed
  }));
};

export const formatLabel = (value = "") => {
  return value.replace(/([a-z])([A-Z])/g, "$1 $2");
}

export const formatNumberForInput = (value: string) => {
  let val = value.replace(/,/g, '');
  if (!isNaN(Number(val))) {
    if (val.includes('.')) {
      const parts = val.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    } else {
      return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }
  return value;
};

export const isDeepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a === null || typeof a !== "object" || b === null || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!isDeepEqual(a[key], b[key])) return false;
  }
  return true;
};
